import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { switchMap, catchError, tap, take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import {
    MC_USER_TYPE,
    MCAuthenticationService,
    MCRootStoreService,
    MCRootDataService,
    MCUserPermission
} from '@maslul/common-services';

export type AuthStatus = 'idle' | 'authenticating' | 'authenticated' | 'unauthorized' | 'error';

@Injectable({ providedIn: 'root' })
export class AuthStateService {

    private router = inject(Router);
    private rootDataService = inject(MCRootDataService);
    private rootStoreService = inject(MCRootStoreService);
    private permissionService = inject(MCAuthenticationService);

    private authStatus$ = new BehaviorSubject<AuthStatus>('idle');
    private authPromise: Observable<boolean> | null = null;

    getAuthStatus(): Observable<AuthStatus> {
        return this.authStatus$.asObservable();
    }

    /**
     * Starts authentication if not already authenticated.
     * Returns an Observable<boolean> that resolves immediately if token exists,
     * or completes once auth finishes.
     * Non-blocking: always returns true to let the route activate.
     */
    ensureAuthenticated(): Observable<boolean> {
        return this.rootStoreService.getToken().pipe(
            take(1),
            switchMap(token => {
                if (token) {
                    this.authStatus$.next('authenticated');
                    return of(true);
                }

                // Start auth in background if not already running
                if (this.authStatus$.value !== 'authenticating') {
                    this.startAuthentication();
                }

                // Return true immediately - don't block the route
                return of(true);
            })
        );
    }

    /**
     * Returns an Observable that completes when authentication is done.
     * Used by HomeComponent to wait for auth before loading data.
     */
    waitForAuth(): Observable<AuthStatus> {
        return this.authStatus$.pipe(
            // Wait until we're past 'authenticating' state
        );
    }

    private startAuthentication() {
        this.authStatus$.next('authenticating');
        console.log('[AuthState] startAuthentication - calling authenticate()');

        this.permissionService.authenticate().pipe(
            tap(() => console.log('[AuthState] authenticate() completed successfully')),
            switchMap(() => {
                console.log('[AuthState] calling getUserPermission()');
                return this.rootDataService.getUserPermission();
            }),
            catchError((error: HttpErrorResponse) => {
                console.error('[AuthState] error during auth flow:', error.status, error.message);
                if (error.status === 403 || error.status === 401) {
                    this.authStatus$.next('unauthorized');
                } else {
                    this.authStatus$.next('error');
                }
                return throwError(() => error);
            })
        ).subscribe({
            next: (userPermission: MCUserPermission) => {
                console.log('[AuthState] getUserPermission returned:', JSON.stringify(userPermission));
                console.log('[AuthState] userType:', userPermission?.permission?.userType, '| MC_USER_TYPE.UNAUTHORIZED:', MC_USER_TYPE.UNAUTHORIZED);
                if (userPermission.permission.userType === MC_USER_TYPE.UNAUTHORIZED) {
                    console.log('[AuthState] -> setting status to unauthorized');
                    this.authStatus$.next('unauthorized');
                } else {
                    console.log('[AuthState] -> setting status to authenticated');
                    this.authStatus$.next('authenticated');
                }
            },
            error: () => {
                console.error('[AuthState] subscribe error (already handled in catchError)');
            }
        });
    }
}
